import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import PropTypes from "prop-types";
import './ActionsOverlay.scss';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

const ActionsOverlay = ({ drillDown, statusData }) => {

    const chartRef = useRef(null);
    const data2 = [
        { name: 'Group A', value: 0 },
        { name: 'Group B', value: 300 },
    ];

    const totalStatusValue = statusData.reduce((acc, entry) => acc + (entry.value || 0), 0);

    let rpcPercentage, nrpcPercentage;

    if (statusData.length === 2) {
        // Case: [{name:rpc, value:34343}, {name:nrpc, value: 444}]
        rpcPercentage = (statusData[0].value / totalStatusValue * 100).toFixed(2);
        nrpcPercentage = (statusData[1].value / totalStatusValue * 100).toFixed(2);
    } else if (statusData.length === 1) {
        // Case: [{name:rpc, value:34343}] or [{name:nrpc, value: 444}]
        const singleEntry = statusData[0];
        rpcPercentage = singleEntry.name === 'RPC' ? 100 : 0;
        nrpcPercentage = singleEntry.name === 'NRPC' ? 100 : 0;
    } else {
        // Default case or Case: [{name:rpc, value:34343}, {name:nrpc, value: 444}, {name:null, value: 556}]
        const validEntries = statusData.filter(entry => entry.value !== null);
        const validTotal = validEntries.reduce((acc, entry) => acc + entry.value, 0);
        rpcPercentage = (validEntries.find(entry => entry.name === 'RPC')?.value / validTotal * 100).toFixed(2);
        nrpcPercentage = (validEntries.find(entry => entry.name === 'NRPC')?.value / validTotal * 100).toFixed(2);
    }

    const rpc = `${rpcPercentage}%`;
    const nrpc = `${nrpcPercentage}%`;

    const COLORS = ['#83ABED', '#F1F4F9', '#FFBB28', '#FF8042'];

    useEffect(() => {
        if (chartRef && chartRef.current) {
            const ctx = chartRef.current.getContext('2d');

            const labels = drillDown.map(item => item.name);
            const values = drillDown.map(item => item.value);
            const colors = [
                'rgba(133, 113, 244, 1)',
                'rgba(9, 114, 121, 1)',
                'rgba(48, 206, 216, 1)',
                'rgba(76, 214, 180, 1)',
                'rgba(131, 60, 202, 1)',
                'rgba(30, 156, 164, 1)'
            ];

            const total = values.reduce((acc, curr) => acc + curr, 0);
            const percentages = values.map(value => ((value / total) * 100).toFixed(2) + '%');

            const myChart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels: labels,
                    datasets: [{
                        data: values,
                        backgroundColor: colors,
                        borderColor: colors,
                        borderWidth: 1
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    return context.label + ': ' + context.formattedValue;
                                }
                            }
                        },
                        legend: {
                            display: false
                        }
                    }
                }
            });

            const legend = document.getElementById('legend');

            legend.innerHTML = '';

            if (legend) {
                labels.forEach((label, index) => {
                    const li = document.createElement('li');
                    li.textContent = `${label} (${percentages[index]})`; // Display label with percentage
                    li.classList.add('amount-modal'); // Add your class name here
                    li.style.color = colors[index % colors.length]; // Set text color to white
                    li.style.listStyleType = 'none'; // Remove default bullet style
                    legend.appendChild(li);

                    // Create a span element for the bullet
                    const bullet = document.createElement('span');
                    bullet.textContent = ''; // No text content needed
                    bullet.classList.add('bullet'); // Add a class for styling
                    bullet.style.borderColor = colors[index % colors.length]; // Set border color
                    li.insertBefore(bullet, li.firstChild); // Insert the bullet before the text content
                });
            }

            return () => {
                myChart.destroy();
            };
        }
    }, [drillDown]);

    return (
        <>
            <div className="actions-overlay-card" id='overlay-action'>
                <div className="actions-overlay-card-header" id='amount-modal-header'>
                    <p>Actions</p>
                </div>
                <div className="actions-overlay-card-body actions-card">
                    {/* <h2>{number}</h2> */}
                    <div className='actions-overlay-card-body__canvas'>
                        <canvas className='actions-overlay' width="200" ref={chartRef}></canvas>
                    </div>
                    <ul id="legend"></ul>
                    <div className='half-donut-actions'>
                        <ResponsiveContainer width={300} height={300}>
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    cx="60%"
                                    cy="40%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    fill="#82ca9d"
                                    startAngle={180}
                                    endAngle={0}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {data2.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div id='status_actions_percentage'>
                            <p>RPC - {rpc}</p>
                            <p>NRPC - {nrpc}</p>
                        </div>
                    </div>
                </div>

            </div>
        </>
    );
};

ActionsOverlay.propTypes = {
    number: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    drillDown: PropTypes.any.isRequired,
    statusData: PropTypes.any.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
    })).isRequired,
};

export default ActionsOverlay;
